// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// =====================================================================================
// D-UI-2 — a funnel on every column of every table.
//
// Patrick, 2026-09-29: "every single col to have the funnel and be filterable ... no exceptions
// for all table for every single table".
//
// The header funnel (ColumnFilter.tsx) already existed, and so did the server registries behind
// the four big lists. What did not exist was a way for the OTHER 150 tables to get one without
// each page hand-writing filter state, distinct-value scans, chips and the paging reset. That is
// this hook: a table that holds its rows in the browser declares its columns once and gets
//
//   fx.rows        the rows that pass every funnel — feed THIS to the pager and the body
//   fx.funnel(k)   the funnel for one header cell
//   fx.chips       the chip row (what is filtered, and how to clear it)
//   fx.sig         a stable signature of the selections, for the pager's reset key
//
// A column says what its cell SHOWS (`value`). The funnel lists and matches exactly that, so
// the option a user ticks is the text they can see in the table.
//
// THE RULE THIS FILE ENFORCES: a funnel filters the WHOLE set the table holds, never the page on
// screen. So the rows passed in are the complete loaded set, and paging happens after.
// A server-paged list does not use this hook: its funnels go to the server (ListTable).
// =====================================================================================

import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  ColumnFilterButton, ColumnFilterChips, applyColumnFilters, columnFilterSig, filterToken,
  funnelToServerOps, toNumberKey, type ColumnFilters,
} from './ColumnFilter'

/** list = tick values; text = contains box; date = from/to; number = min/max. */
export type FunnelKind = 'list' | 'text' | 'date' | 'number'

export interface FunnelColumn<T> {
  key: string
  label: string
  /** What the cell shows. Dates may be given raw (ISO string or Date); they compare by day. */
  value: (row: T) => unknown
  /** Omit to infer from the values: all dates -> date, many distinct numbers -> number, else list. */
  kind?: FunnelKind
  /** Fired when this column's funnel opens: lets a page load values it resolves on demand. */
  onOpen?: () => void
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:$|[T ]\d{2}:\d{2})/
// What a formatted date looks like in a rendered cell: 9/28/2026, 28.09.2026, Sep 28, 2026.
const SHOWN_DATE = /^(?:\d{1,2}[/.]\d{1,2}[/.]\d{2,4}|[A-Z][a-z]{2,8}\.? \d{1,2},? \d{4})(?:$|[ ,])/
/** Past this many distinct numbers a tick list is a worse control than Min/Max. */
export const NUMBER_LIST_MAX = 12

const isBlank = (v: unknown) =>
  v === null || v === undefined || (typeof v === 'string' && (v.trim() === '' || v.trim() === '—'))

/**
 * A cell as a LOCAL calendar day (YYYY-MM-DD), or null.
 *
 * Local on purpose. The table shows the user's own date; a filter that compared in UTC would put
 * a delivery made at 01:00 in Cairo on the previous day and drop it from "from the 28th".
 * A bare YYYY-MM-DD carries no time, so it is taken as written.
 */
export function localDateKey(v: unknown): string | null {
  if (isBlank(v)) return null
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim())) return v.trim()
  const d = v instanceof Date ? v : new Date(String(v))
  if (isNaN(d.getTime())) return null
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Which control a column gets, from the values it holds. Blank cells do not vote. */
export function inferKind(values: Iterable<unknown>): FunnelKind {
  let seen = 0, dates = 0, nums = 0
  const distinct = new Set<string>()
  for (const v of values) {
    if (isBlank(v)) continue
    seen++
    const s = v instanceof Date ? '' : String(v).trim()
    if (distinct.size <= NUMBER_LIST_MAX) distinct.add(s)
    if (v instanceof Date || ISO_DATE.test(s) || (SHOWN_DATE.test(s) && localDateKey(s) !== null)) dates++
    else if (toNumberKey(v) !== null) nums++
  }
  if (seen === 0) return 'list'
  if (dates === seen) return 'date'
  if (nums === seen && distinct.size > NUMBER_LIST_MAX) return 'number'
  return 'list'
}

/** The distinct, sorted tokens of one column; blanks last. */
export function distinctTokens(values: Iterable<unknown>): string[] {
  const set = new Set<string>()
  for (const v of values) set.add(filterToken(isBlank(v) ? null : v))
  const blank = filterToken(null)
  const out = [...set].filter(t => t !== blank)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
  if (set.has(blank)) out.push(blank)
  return out
}

/** The value a filter compares, per kind: a day for dates, the cell itself otherwise. */
export function comparable(kind: FunnelKind, v: unknown): unknown {
  if (isBlank(v)) return null
  return kind === 'date' ? localDateKey(v) : v instanceof Date ? localDateKey(v) : v
}

export interface TableFunnels<T> {
  /** Rows that pass every funnel. Page and render THESE. */
  rows: T[]
  /** How many rows came in, before the funnels. */
  total: number
  filters: ColumnFilters
  setFilters: (next: ColumnFilters) => void
  /** The funnel for one header cell; null for a key that was not declared. */
  funnel: (key: string) => ReactNode
  /** Chip row + "n of m" count; null while nothing is filtered. */
  chips: ReactNode
  /** Stable signature of the selections — pass as the pager's reset key. */
  sig: string
  active: boolean
  /** Spread on the <table>: tells the automatic layer this table has its own funnels. */
  tableProps: { 'data-funnels': 'own' }
}

export function useTableFunnels<T>(rows: T[] | null | undefined, columns: FunnelColumn<T>[]): TableFunnels<T> {
  const all = rows ?? []
  const [filters, setFilters] = useState<ColumnFilters>({})

  const byKey = useMemo(() => new Map(columns.map(c => [c.key, c])), [columns])

  // One pass per column, only when the rows or the column set change.
  const kinds = useMemo(() => {
    const out = new Map<string, FunnelKind>()
    for (const c of columns) {
      if (c.kind) { out.set(c.key, c.kind); continue }
      const sample = all.length > 400 ? all.slice(0, 400) : all
      out.set(c.key, inferKind(sample.map(r => c.value(r))))
    }
    return out
  }, [all, columns])

  const getValue = useCallback((row: T, key: string) => {
    const c = byKey.get(key)
    return c ? comparable(kinds.get(key) ?? 'list', c.value(row)) : null
  }, [byKey, kinds])

  // A filter on a column the table no longer has (the column set changed) must not keep hiding
  // rows with no control left to clear it.
  const live = useMemo(() => {
    const out: ColumnFilters = {}
    for (const [k, s] of Object.entries(filters)) if (byKey.has(k) && s.size > 0) out[k] = s
    return out
  }, [filters, byKey])

  const filtered = useMemo(() => applyColumnFilters(all, live, getValue), [all, live, getValue])
  const active = Object.keys(live).length > 0
  const labels = useMemo(() => Object.fromEntries(columns.map(c => [c.key, c.label])), [columns])

  const funnel = (key: string): ReactNode => {
    const c = byKey.get(key)
    if (!c) return null
    const kind = kinds.get(key) ?? 'list'
    return (
      <ColumnFilterButton
        label={c.label}
        isDate={kind === 'date'}
        isNumeric={kind === 'number'}
        isText={kind === 'text'}
        onOpen={c.onOpen}
        // Spreadsheet semantics: a column keeps its own full option list while it is filtered;
        // every OTHER funnel narrows it, so an option that would return nothing is not offered.
        getValues={() => {
          const others: ColumnFilters = { ...live }
          delete others[key]
          return distinctTokens(applyColumnFilters(all, others, getValue).map(r => c.value(r)))
        }}
        selected={live[key] ?? new Set()}
        onChange={next => setFilters({ ...live, [key]: next })} />
    )
  }

  const chips: ReactNode = active ? (
    <div data-funnel-chips>
      <ColumnFilterChips
        filters={live}
        labels={labels}
        onClear={k => setFilters({ ...live, [k]: new Set() })}
        onClearAll={() => setFilters({})} />
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '0 0 6px' }}>
        {filtered.length.toLocaleString()} of {all.length.toLocaleString()} rows match
      </div>
    </div>
  ) : null

  return {
    rows: filtered, total: all.length, filters: live, setFilters, funnel, chips,
    sig: columnFilterSig(live), active, tableProps: { 'data-funnels': 'own' },
  }
}

// =====================================================================================
// SERVER-PAGED lists without a column registry (facilities, deliveries, document tracker ...).
//
// The rows on screen are ONE page, so the funnel cannot filter them: it collects what the user
// asked for and hands back `filter=` values for the endpoint, which applies them in its query
// (functions/Common/SimpleListFilters.cs). Nothing is enumerated from the server:
//   text    a contains box
//   date    from / to
//   number  min / max
//   list    a tick list of values the PAGE already knows (a status vocabulary, a fixed enum)
// =====================================================================================

export interface ServerFunnelColumn {
  /** The key the endpoint's allow-list knows this column by. */
  key: string
  label: string
  kind: FunnelKind
  /** kind 'list': the values offered. `label` is what the user reads. */
  options?: (string | { value: string; label: string })[]
}

export interface ServerFunnels {
  /** `key:op:value` strings, ready to append as repeated `filter` query parameters. */
  filter: string[]
  /** Stable signature: put it in the pager's deps so a change refetches and returns to page 1. */
  sig: string
  funnel: (key: string) => ReactNode
  chips: ReactNode
  active: boolean
  clear: () => void
  tableProps: { 'data-funnels': 'own' }
}

export function useServerFunnels(columns: ServerFunnelColumn[]): ServerFunnels {
  const [filters, setFilters] = useState<ColumnFilters>({})
  const byKey = useMemo(() => new Map(columns.map(c => [c.key, c])), [columns])

  const live = useMemo(() => {
    const out: ColumnFilters = {}
    for (const [k, s] of Object.entries(filters)) if (byKey.has(k) && s.size > 0) out[k] = s
    return out
  }, [filters, byKey])

  const filter = useMemo(() => {
    const out: string[] = []
    for (const [key, set] of Object.entries(live)) {
      const c = byKey.get(key)!
      const ops = c.kind === 'text' ? ['contains'] : c.kind === 'list' ? ['in'] : ['gte', 'lte']
      for (const p of funnelToServerOps(set, ops).preds) out.push(`${key}:${p.op}:${p.values.join(',')}`)
    }
    return out.sort()
  }, [live, byKey])

  const labelOf = useCallback((key: string, value: string) => {
    const o = byKey.get(key)?.options?.find(x => (typeof x === 'string' ? x : x.value) === value)
    return o === undefined ? undefined : typeof o === 'string' ? o : o.label
  }, [byKey])

  const funnel = (key: string): ReactNode => {
    const c = byKey.get(key)
    if (!c) return null
    return (
      <ColumnFilterButton
        label={c.label}
        isDate={c.kind === 'date'}
        isNumeric={c.kind === 'number'}
        isText={c.kind === 'text'}
        values={c.kind === 'list' ? (c.options ?? []).map(o => (typeof o === 'string' ? o : o.value)) : []}
        labelFor={c.kind === 'list' ? (v: string) => labelOf(key, v) : undefined}
        selected={live[key] ?? new Set()}
        onChange={next => setFilters({ ...live, [key]: next })} />
    )
  }

  const active = Object.keys(live).length > 0
  const chips: ReactNode = active ? (
    <div data-funnel-chips>
      <ColumnFilterChips
        filters={live}
        labels={Object.fromEntries(columns.map(c => [c.key, c.label]))}
        labelFor={labelOf}
        onClear={k => setFilters({ ...live, [k]: new Set() })}
        onClearAll={() => setFilters({})} />
    </div>
  ) : null

  return {
    filter, sig: filter.join('|'), funnel, chips, active,
    clear: () => setFilters({}), tableProps: { 'data-funnels': 'own' },
  }
}
